// React imports
import { useReducer, useCallback } from 'react';

// Types
import { ApplicationType, ConfiguredDiscount, SaveConfiguredDiscount } from '@spike/discount-model';
import AddOnService from '@spike/addon-service-model';
import { Product, BusinessProduct } from '@spike/product-model';
import { ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector';
import { TagOption } from 'components/UI/V3/TagsSelector/TagOption';
import Service from 'model/Service';

export type TieredDiscountType = 'amount' | 'percentage';

export interface EnhancedBusinessProduct extends BusinessProduct {
    productId?: number;
}

export interface EnhancedProduct extends Product {
    businessProduct?: EnhancedBusinessProduct;
}

interface DiscountFormState {
    discountType: string;
    discountName: string;
    selectedClientTags: Array<TagOption<string>>;
    selectedGroomingServices: Service[];
    selectedDaycareServices: Service[];
    selectedBoardingServices: Service[];
    selectedAddons: AddOnService[];
    selectedTransportationServices: Service[];
    selectedEvaluationServices: Service[];
    selectedProducts: EnhancedProduct[];
    applyDiscountTo: string;
    discountValue: ValueTypeSelectorValue;
    tieredDiscountType: TieredDiscountType;
    tieredDiscountValues: number[];
}

type DiscountFormAction =
    | { type: 'SET_DISCOUNT_TYPE'; payload: string }
    | { type: 'SET_DISCOUNT_NAME'; payload: string }
    | { type: 'SET_SELECTED_CLIENT_TAGS'; payload: Array<TagOption<string>> }
    | { type: 'SET_SELECTED_GROOMING_SERVICES'; payload: Service[] }
    | { type: 'SET_SELECTED_DAYCARE_SERVICES'; payload: Service[] }
    | { type: 'SET_SELECTED_BOARDING_SERVICES'; payload: Service[] }
    | { type: 'SET_SELECTED_ADDONS'; payload: AddOnService[] }
    | { type: 'SET_SELECTED_TRANSPORTATION_SERVICES'; payload: Service[] }
    | { type: 'SET_SELECTED_EVALUATION_SERVICES'; payload: Service[] }
    | { type: 'SET_SELECTED_PRODUCTS'; payload: EnhancedProduct[] }
    | { type: 'SET_APPLY_DISCOUNT_TO'; payload: string }
    | { type: 'SET_DISCOUNT_VALUE'; payload: ValueTypeSelectorValue }
    | { type: 'SET_TIERED_DISCOUNT_TYPE'; payload: TieredDiscountType }
    | { type: 'SET_TIERED_DISCOUNT_VALUES'; payload: number[] };

interface UseDiscountFormReturn {
    // Form state
    discountType: string;
    discountName: string;
    selectedClientTags: Array<TagOption<string>>;
    selectedGroomingServices: Service[];
    selectedDaycareServices: Service[];
    selectedBoardingServices: Service[];
    selectedAddons: AddOnService[];
    selectedTransportationServices: Service[];
    selectedEvaluationServices: Service[];
    selectedProducts: EnhancedProduct[];
    applyDiscountTo: string;
    discountValue: ValueTypeSelectorValue;
    tieredDiscountType: TieredDiscountType;
    tieredDiscountValues: number[];

    // Setters
    setDiscountType: (type: string) => void;
    setDiscountName: (name: string) => void;
    setSelectedClientTags: (tags: Array<TagOption<string>>) => void;
    setSelectedGroomingServices: (services: Service[]) => void;
    setSelectedDaycareServices: (services: Service[]) => void;
    setSelectedBoardingServices: (services: Service[]) => void;
    setSelectedAddons: (addons: AddOnService[]) => void;
    setSelectedTransportationServices: (services: Service[]) => void;
    setSelectedEvaluationServices: (services: Service[]) => void;
    setSelectedProducts: (products: EnhancedProduct[]) => void;
    setApplyDiscountTo: (value: string) => void;
    setDiscountValue: (value: ValueTypeSelectorValue) => void;

    // Tiered discount handlers
    setTieredDiscountType: (type: TieredDiscountType) => void;
    setTieredDiscountValues: (values: number[]) => void;
}

interface UseDiscountFormOptions {
    initialDiscountName?: string;
    editDiscount: SaveConfiguredDiscount | null;
}

const getInitialState = (options?: UseDiscountFormOptions): DiscountFormState => {
    const initialDiscountType =
        options?.editDiscount?.applicationType === ApplicationType.CUSTOMER_TAG ? 'Client Tag' : 'Multi Pet Discount';

    return {
        discountType: initialDiscountType,
        discountName: options?.initialDiscountName || '',
        selectedClientTags: [],
        selectedGroomingServices: [],
        selectedDaycareServices: [],
        selectedBoardingServices: [],
        selectedTransportationServices: [],
        selectedEvaluationServices: [],
        selectedAddons: [],
        selectedProducts: [],
        applyDiscountTo: 'Additional Pet',
        discountValue: {
            type: 'amount',
            value: 0
        },
        tieredDiscountType: 'amount',
        tieredDiscountValues: [0, 0, 0, 0]
    };
};

const discountFormReducer = (state: DiscountFormState, action: DiscountFormAction): DiscountFormState => {
    switch (action.type) {
        case 'SET_DISCOUNT_TYPE':
            return { ...state, discountType: action.payload };
        case 'SET_DISCOUNT_NAME':
            return { ...state, discountName: action.payload };
        case 'SET_SELECTED_CLIENT_TAGS':
            return { ...state, selectedClientTags: action.payload };
        case 'SET_SELECTED_GROOMING_SERVICES':
            return { ...state, selectedGroomingServices: action.payload };
        case 'SET_SELECTED_DAYCARE_SERVICES':
            return { ...state, selectedDaycareServices: action.payload };
        case 'SET_SELECTED_BOARDING_SERVICES':
            return { ...state, selectedBoardingServices: action.payload };
        case 'SET_SELECTED_ADDONS':
            return { ...state, selectedAddons: action.payload };
        case 'SET_SELECTED_TRANSPORTATION_SERVICES':
            return { ...state, selectedTransportationServices: action.payload };
        case 'SET_SELECTED_EVALUATION_SERVICES':
            return { ...state, selectedEvaluationServices: action.payload };
        case 'SET_SELECTED_PRODUCTS':
            return { ...state, selectedProducts: action.payload };
        case 'SET_APPLY_DISCOUNT_TO':
            return { ...state, applyDiscountTo: action.payload };
        case 'SET_DISCOUNT_VALUE':
            return { ...state, discountValue: action.payload };
        case 'SET_TIERED_DISCOUNT_TYPE':
            return { ...state, tieredDiscountType: action.payload };
        case 'SET_TIERED_DISCOUNT_VALUES':
            return { ...state, tieredDiscountValues: action.payload };
        default:
            return state;
    }
};

/**
 * useDiscountForm - Centralized Form State Management Hook
 *
 * Purpose:
 * Manages all form state for the Add/Edit Discount form using a reducer pattern.
 * Provides a single source of truth for all discount configuration inputs.
 *
 * Responsibilities:
 * - Manages discount type selection (Multi Pet Discount vs Client Tag)
 * - Handles discount name input
 * - Tracks selected client tags for tag-based discounts
 * - Maintains selected items across all service categories (grooming, daycare, boarding, transportation, evaluation)
 * - Manages selected add-ons and products
 * - Controls "Apply Discount To" option (Additional Pet, All Pets, Tiered)
 * - Stores discount values (amount or percentage for simple discounts)
 * - Manages tiered discount configuration (type and values for 2nd, 3rd, 4th+ pets)
 *
 * Key Features:
 * - Uses useReducer for predictable state updates and better performance
 * - Provides memoized setter functions to prevent unnecessary re-renders
 * - Supports initial values (useful for edit mode)
 * - Type-safe state management with TypeScript interfaces
 * - Organized state structure with clear separation of concerns
 *
 * State Structure:
 * - Discount configuration: type, name, tags
 * - Item selections: services by category, add-ons, products
 * - Value configuration: discount application method and values
 * - Tiered settings: separate type and values array for multi-pet tiers
 *
 * When to use:
 * Use this hook in the AddDiscount component to manage all form inputs.
 * It provides both state values and setter functions for complete form control.
 *
 * @param {UseDiscountFormOptions} options - Optional configuration (e.g., initial discount name for edit mode)
 * @returns {UseDiscountFormReturn} Object containing all form state and setter functions
 */
export const useDiscountForm = (options?: UseDiscountFormOptions): UseDiscountFormReturn => {
    const [state, dispatch] = useReducer(discountFormReducer, getInitialState(options));

    const setDiscountType = useCallback((payload: string) => {
        dispatch({ type: 'SET_DISCOUNT_TYPE', payload });
    }, []);

    const setDiscountName = useCallback((payload: string) => {
        dispatch({ type: 'SET_DISCOUNT_NAME', payload });
    }, []);

    const setSelectedClientTags = useCallback((payload: Array<TagOption<string>>) => {
        dispatch({ type: 'SET_SELECTED_CLIENT_TAGS', payload });
    }, []);

    const setSelectedGroomingServices = useCallback((payload: Service[]) => {
        dispatch({ type: 'SET_SELECTED_GROOMING_SERVICES', payload });
    }, []);

    const setSelectedDaycareServices = useCallback((payload: Service[]) => {
        dispatch({ type: 'SET_SELECTED_DAYCARE_SERVICES', payload });
    }, []);

    const setSelectedBoardingServices = useCallback((payload: Service[]) => {
        dispatch({ type: 'SET_SELECTED_BOARDING_SERVICES', payload });
    }, []);

    const setSelectedAddons = useCallback((payload: AddOnService[]) => {
        dispatch({ type: 'SET_SELECTED_ADDONS', payload });
    }, []);

    const setSelectedTransportationServices = useCallback((payload: Service[]) => {
        dispatch({ type: 'SET_SELECTED_TRANSPORTATION_SERVICES', payload });
    }, []);

    const setSelectedEvaluationServices = useCallback((payload: Service[]) => {
        dispatch({ type: 'SET_SELECTED_EVALUATION_SERVICES', payload });
    }, []);

    const setSelectedProducts = useCallback((payload: EnhancedProduct[]) => {
        dispatch({ type: 'SET_SELECTED_PRODUCTS', payload });
    }, []);

    const setApplyDiscountTo = useCallback((payload: string) => {
        dispatch({ type: 'SET_APPLY_DISCOUNT_TO', payload });
    }, []);

    const setDiscountValue = useCallback((payload: ValueTypeSelectorValue) => {
        dispatch({ type: 'SET_DISCOUNT_VALUE', payload });
    }, []);

    const setTieredDiscountType = useCallback((payload: TieredDiscountType) => {
        dispatch({ type: 'SET_TIERED_DISCOUNT_TYPE', payload });
    }, []);

    const setTieredDiscountValues = useCallback((payload: number[]) => {
        dispatch({ type: 'SET_TIERED_DISCOUNT_VALUES', payload });
    }, []);

    return {
        ...state,
        setDiscountType,
        setDiscountName,
        setSelectedClientTags,
        setSelectedGroomingServices,
        setSelectedDaycareServices,
        setSelectedBoardingServices,
        setSelectedAddons,
        setSelectedTransportationServices,
        setSelectedEvaluationServices,
        setSelectedProducts,
        setApplyDiscountTo,
        setDiscountValue,
        setTieredDiscountType,
        setTieredDiscountValues
    };
};
